/**
 * API client for the remote adaptive screener: token validation, birthday
 * verification, administration state (kind + composed sections + saved answers),
 * and per-answer save. Mirrors the fixed-survey client (`../api`) and reuses its
 * `SurveyError` discriminator so terminal-state mapping stays in one place.
 * @agentic members
 */

import type { Questionnaire } from "@medplum/fhirtypes";
import { SurveyError, type SurveyErrorReason } from "../api";

const API_BASE = import.meta.env["VITE_API_URL"] || window.location.origin;

type ErrorResponse = {
  error: string;
  reason?: SurveyErrorReason;
  locked?: boolean;
};

const parseErrorResponse = async (res: Response): Promise<SurveyError> => {
  const text = await res.text().catch(() => "");
  try {
    const body = JSON.parse(text) as ErrorResponse;
    return new SurveyError(body.error, false, body.reason);
  } catch {
    return new SurveyError(text || `Request failed (${res.status})`, false);
  }
};

export type ScreenerValidateResponse = {
  /**
   * Whether the date-of-birth gate must be cleared. Deliberately the only field:
   * no welcome/advocate context is disclosed before the gate (D16) — that is
   * resolved at `state`, after verification.
   */
  requiresBirthdayVerification: boolean;
};

export type ScreenerVerifyResponse = { verified: boolean; token: string };

/** One instrument within a composed section. */
export type ScreenerInstrument = { key: string; questionnaireUrl: string };

/** A composed screener section, in administration order. */
export type ScreenerSection = {
  key: string;
  order: number;
  gating: string;
  reasonCode: string;
  reason: string;
  instruments: ScreenerInstrument[];
};

export type ScreenerAnswerRecord = {
  items: Array<{ linkId?: string; answer?: unknown[] }>;
  status: string;
};

/**
 * The server's screen decision for a returning patient (D18/D11): resume the
 * walk, show results (completed), or show the generic expired screen (closed).
 */
export type ScreenerEntry = "question" | "completed" | "closed";

/**
 * A section's server-recorded lifecycle state (PRD §8.8) — rendered, never
 * re-derived, by this client (D18).
 */
export type ScreenerSectionState =
  | "complete"
  | "partial-abandoned"
  | "partial-declined"
  | "skipped"
  | "not-reached"
  | "not-clinically-appropriate";

export type ScreenerStateResponse = {
  /** True when kind resolution failed — the client shows a fail-visible screen. */
  kindError?: boolean;
  /**
   * Adults-only fail-safe (D14): `false` when the patient is not a confirmed
   * adult, so the client shows an unavailable state rather than a (vacuously
   * complete) results screen. Absent/`true` means eligible.
   */
  eligible?: boolean;
  kind?: "first-check-in" | "follow-up";
  isBaseline?: boolean;
  sections?: ScreenerSection[];
  order?: string[];
  questionnaires?: Questionnaire[];
  answers?: Record<string, ScreenerAnswerRecord>;
  /** Server-authoritative completion: true when the administration is finished. */
  resume?: { questionnaireUrl: string | null; complete: boolean };
  /** The server's entry decision (absent on a pre-lifecycle server → MVP flow). */
  entry?: ScreenerEntry;
  /** Server-recorded five-state map, keyed by section key (§8.8, D18). */
  sectionStates?: Record<string, ScreenerSectionState>;
  /** Explicitly skipped link ids per instrument URL (§8.4) — never re-offered on resume. */
  skips?: Record<string, string[]>;
  /** Explicit decline settlements per instrument URL — settled sections are never re-offered (D10). */
  settlements?: Record<string, string>;
  /** D12: false when the administration completed with no answers (all skipped) — no results. */
  started?: boolean;
  /** Advocate display name resolved server-side post-verification (D16), if any. */
  advocateDisplayName?: string;
  /**
   * Present when a positive self-harm answer exists for the administration. The
   * server re-drives the (idempotent) escalation on every state load; `delivered`
   * is `false` when it is still undelivered, so the client holds the crisis
   * screen with a retry across reopens/new devices (D2/D14).
   */
  safety?: { triggered: boolean; delivered: boolean };
};

export type ScreenerAnswerItem = {
  linkId: string;
  text?: string;
  answer: Array<{
    valueInteger?: number;
    valueString?: string;
    valueCoding?: { code: string; display: string };
  }>;
};

export type ScreenerAnswerResponse = {
  saved: boolean;
  questionnaireResponseId?: string;
  status: string;
  safetyTriggered: boolean;
  /**
   * Present only when `safetyTriggered`: whether the acute-risk escalation (risk
   * Observation + advocate notification) was fully delivered. `false` means the
   * client must hold the crisis screen and offer a retry — the escalation is
   * idempotent, so a retried save re-drives it (D2/D14).
   */
  escalationDelivered?: boolean;
};

/** Validate a screener token; reports whether the DOB gate must be cleared. */
export const validateScreener = async (
  token: string,
): Promise<ScreenerValidateResponse> => {
  const res = await fetch(
    `${API_BASE}/surveys/screener/validate?token=${encodeURIComponent(token)}`,
  );
  if (!res.ok) {
    throw await parseErrorResponse(res);
  }
  return res.json() as Promise<ScreenerValidateResponse>;
};

/** Verify identity via date of birth; returns a verified, submit-capable token. */
export const verifyScreenerBirthday = async (
  token: string,
  birthDate: string,
): Promise<ScreenerVerifyResponse> => {
  const res = await fetch(`${API_BASE}/surveys/screener/verify-birthday`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, birthDate }),
  });
  if (!res.ok) {
    throw await parseErrorResponse(res);
  }
  return res.json() as Promise<ScreenerVerifyResponse>;
};

/**
 * Fetch the administration state: kind, composed sections, and saved answers.
 * POSTs the verified submit token in the body — never a query string, which
 * access logs, browser history, and Referer headers can retain (the token
 * authorizes PHI reads and writes until expiry). Matches `/answer`/`/settle`.
 */
export const getScreenerState = async (
  token: string,
): Promise<ScreenerStateResponse> => {
  const res = await fetch(`${API_BASE}/surveys/screener/state`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    throw await parseErrorResponse(res);
  }
  return res.json() as Promise<ScreenerStateResponse>;
};

/**
 * Persist the answers — and any explicit per-question skips (§8.4) — for one
 * section instrument (idempotent upsert, D6). Completion is decided by the
 * server from the merged answer set, so the client sends only the answers and
 * skips — never a trusted `complete` flag. A skip-only save (no items) is
 * valid; the server records the skip as asked-and-not-answered.
 */
export const saveScreenerAnswer = async (
  token: string,
  questionnaireUrl: string,
  items: ScreenerAnswerItem[],
  skippedLinkIds?: string[],
): Promise<ScreenerAnswerResponse> => {
  const res = await fetch(`${API_BASE}/surveys/screener/answer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token,
      questionnaireUrl,
      items,
      ...(skippedLinkIds && skippedLinkIds.length > 0
        ? { skippedLinkIds }
        : {}),
    }),
  });
  if (!res.ok) {
    throw await parseErrorResponse(res);
  }
  return res.json() as Promise<ScreenerAnswerResponse>;
};

export type ScreenerSettleResponse = {
  settled: boolean;
  entry?: ScreenerEntry;
  sectionStates?: Record<string, ScreenerSectionState>;
  settlements?: Record<string, string>;
  resume?: { questionnaireUrl: string | null; complete: boolean };
  started?: boolean;
};

/**
 * One-action section decline (§8.5–§8.6, D10): the server settles every
 * not-yet-resolved instrument the section requires this visit and derives the
 * recorded kind (Skipped vs Partial — declined) from the persisted answers —
 * the client never asserts the clinical distinction (D18). The response carries
 * the post-settle truth the walk advances from.
 */
export const settleScreenerSection = async (
  token: string,
  sectionKey: string,
): Promise<ScreenerSettleResponse> => {
  const res = await fetch(`${API_BASE}/surveys/screener/settle`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, sectionKey }),
  });
  if (!res.ok) {
    throw await parseErrorResponse(res);
  }
  return res.json() as Promise<ScreenerSettleResponse>;
};
